import { useEffect, useRef } from 'react'
import type { DeviceSpecDevice } from '../../types'
import { CHANNELS, channelDef } from './catalog'
import type { SpecIssues } from './model'
import { changeChannelType } from './model'

// 선택한 디바이스의 전체 설정 — LiveEdge 디바이스 등록 화면과 같은 채널 필드 (catalog 기준).
// 시트 머리 줄에 없는 칸(메모·설명·채널 상세)은 여기서만 고친다.
// 위 = 설비조사 단계 칸 (명세 전용·프로토콜 포함 — 프로토콜은 태그 열 구성도 정한다), 아래 = Edge 전송 때 채우는 칸 (LiveEdge 로 가는 값).
export default function DevicePanel({ device, index, count, issues, showIssues, onChange, onMove, onDuplicate, onRemove, onClose }: {
  device: DeviceSpecDevice
  index: number
  count: number
  issues: SpecIssues
  showIssues: boolean
  onChange: (update: (d: DeviceSpecDevice) => DeviceSpecDevice) => void
  onMove: (delta: -1 | 1) => void
  onDuplicate: () => void
  onRemove: () => void
  onClose: () => void
}) {
  // 열리면 첫 입력칸으로 — 서랍(팝업)이 뜨면 키 입력은 서랍이 받는다
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const id = requestAnimationFrame(() => rootRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus())
    return () => cancelAnimationFrame(id)
  }, [])

  const def = channelDef(device.channelType)
  const de = showIssues ? issues.device[device.key] ?? {} : {}
  const set = (patch: Partial<DeviceSpecDevice>) => onChange(d => ({ ...d, ...patch }))
  const setChannel = (key: string, value: string) => onChange(d => ({ ...d, channel: { ...d.channel, [key]: value } }))

  const input = (invalid = false) =>
    `w-full border rounded-lg px-2.5 py-1.5 text-sm ${invalid ? 'border-red-400 bg-red-50/60' : 'border-gray-300'}`

  return (
    <div ref={rootRef} className="bg-surface rounded-xl border border-gray-200 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-800 truncate">{device.name || '이름 없는 디바이스'}</h3>
        <span className="text-xs text-gray-500">#{index + 1}</span>
        <div className="ml-auto flex items-center gap-1">
          <IconButton label="위로" disabled={index === 0} onClick={() => onMove(-1)} d="M4 10l4-4 4 4" />
          <IconButton label="아래로" disabled={index === count - 1} onClick={() => onMove(1)} d="M4 6l4 4 4-4" />
          <IconButton label="닫기 (Esc)" onClick={onClose} d="M4 4l8 8M12 4l-8 8" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <Field label="설비구분"><input value={device.category} onChange={e => set({ category: e.target.value })} className={input()} /></Field>
        <Field label="제조사"><input value={device.maker} onChange={e => set({ maker: e.target.value })} className={input()} /></Field>
        <div className="col-span-2">
          <Field label="모델"><input value={device.model} onChange={e => set({ model: e.target.value })} className={input()} /></Field>
        </div>
        <div className="col-span-2">
          <Field label="디바이스 이름" required error={de.name}>
            <input value={device.name} onChange={e => set({ name: e.target.value })} className={input(!!de.name)} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="프로토콜" error={de.channelType}>
            <select value={device.channelType} onChange={e => onChange(d => changeChannelType(d, e.target.value))} className={input(!!de.channelType)}>
              <option value="">선택하세요</option>
              {CHANNELS.map(c => <option key={c.type} value={c.type}>{c.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="메모 (명세 전용)">
            <textarea value={device.memo} onChange={e => set({ memo: e.target.value })} rows={2} className={input()} />
          </Field>
        </div>
      </div>

      {/* 아래부터는 서버 세팅(Edge 전송) 때 채우는 칸 — 설비조사 단계에서는 비워 둔다 */}
      <div className="pt-3 border-t border-gray-100 space-y-2.5">
        <div className="text-[11px] font-medium text-gray-500">Edge 전송 때 입력</div>
        <Field label="디바이스 코드" error={de.code}>
          <input value={device.code} onChange={e => set({ code: e.target.value })} className={`${input(!!de.code)} font-mono`} />
        </Field>
        <Field label="설명 (LiveEdge 디바이스 설명으로 전송)">
          <input value={device.description} onChange={e => set({ description: e.target.value })} className={input()} />
        </Field>
        {def ? (
          <div className="grid grid-cols-2 gap-2.5">
            {def.fields.filter(f => !f.when || f.when(device.channel)).map(f => {
              const value = device.channel[f.key] ?? ''
              const invalid = !!de.endpoint && (f.key === 'ip' || f.key === 'serverUri' || f.key === 'filePath')
              const notNumber = f.kind === 'int' && value !== '' && !/^\d+$/.test(value) // 점검과 무관하게 늘 알린다
              return (
                <Field key={f.key} label={f.label} error={invalid ? de.endpoint : notNumber ? '숫자만' : undefined}>
                  {f.kind === 'enum' ? (
                    <select value={value} onChange={e => setChannel(f.key, e.target.value)} className={input()}>
                      {!f.options!.includes(value) && <option value={value}>{value || '-'}</option>}
                      {f.options!.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input value={value} onChange={e => setChannel(f.key, e.target.value)}
                      type={f.secret ? 'password' : 'text'} inputMode={f.kind === 'int' ? 'numeric' : undefined}
                      autoComplete={f.secret ? 'new-password' : 'off'} placeholder={f.defaultValue || undefined}
                      className={`${input(invalid || notNumber)} font-mono`} />
                  )}
                </Field>
              )
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-500">위에서 프로토콜을 고르면 LiveEdge 등록 화면과 같은 채널 설정이 나옵니다.</p>
        )}
        {def && <p className="text-[11px] text-gray-500">태그 주소: {def.addressHint}</p>}
      </div>

      <div className="flex items-center gap-3 pt-1 text-xs">
        <button onClick={onDuplicate} className="text-gray-500 hover:text-accent transition">복제</button>
        <button onClick={() => { if (device.tags.length === 0 || confirm(`'${device.name || '디바이스'}' 와 태그 ${device.tags.length}개를 지울까요?`)) onRemove() }}
          className="text-red-600/80 hover:text-red-600 transition">디바이스 삭제</button>
      </div>
    </div>
  )
}

function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[11px] text-gray-500 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
        {error && <span className="text-red-500 ml-1">· {error}</span>}
      </span>
      {children}
    </label>
  )
}

function IconButton({ label, disabled, onClick, d }: { label: string; disabled?: boolean; onClick: () => void; d: string }) {
  return (
    <button onClick={onClick} disabled={disabled} aria-label={label} title={label}
      className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent">
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d={d} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}
